import { RetryLink } from '@apollo/client/link/retry'
import { ErrorLink } from '@apollo/client/link/error'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  HttpLink,
} from '@apollo/client'
import { concatPagination } from '@apollo/client/utilities'
import { envVariables } from './env-variables'
import { logger, LogContext } from './logger'

const APOLLO_URI = `${envVariables.API_BASE_URL}/gql`

// every request is debug level, faro's fetch tracing already records timings and failures in production
const wrappedFetch =
  (context: LogContext.APOLLO_CORE | LogContext.APOLLO_CMS): typeof fetch =>
  async (...pl) => {
    const [, options] = pl
    if (!options?.body) return fetch(...pl)
    try {
      const body = JSON.parse(options.body as string)
      logger.debug(context, 'GraphQL request', { operation: body?.operationName || 'anonymous' })
    } catch {
      // a body we can't parse still gets sent, it just isn't logged
    }
    return fetch(...pl)
  }

// errorPolicy 'all' hands graphql errors back as data, so without this they'd never be seen in monitoring
const errorLink = new ErrorLink(({ error, operation }) => {
  const context = operation.getContext().clientName === 'cms' ? LogContext.APOLLO_CMS : LogContext.APOLLO_CORE
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((graphQLError) => {
      logger.warn(context, 'GraphQL error', {
        operation: operation.operationName,
        message: graphQLError.message,
        path: graphQLError.path?.join('.'),
        code: graphQLError.extensions?.code as string | undefined,
      })
    })
    return
  }
  logger.warn(context, 'GraphQL network error', { operation: operation.operationName }, error)
})

const cmsLink = new HttpLink({
  uri: envVariables.APP_CMS,
  fetch: wrappedFetch(LogContext.APOLLO_CMS),
})

const getLink = (): ApolloLink => {
  const httpLink = createHttpLink({
    uri: APOLLO_URI,
    fetch: wrappedFetch(LogContext.APOLLO_CORE),
  })

  const retryLink = new RetryLink({
    attempts: {
      max: 30,
      retryIf: (error, operation) => {
        const isError = Boolean(error)
        if (isError) logger.debug(LogContext.APOLLO_CORE, 'Retrying GraphQL request', { operation: operation.operationName })
        return isError
      },
    },
    delay: {
      initial: 200,
      max: Infinity,
      jitter: true,
    },
  })

  const directionalLink = new RetryLink().split(
    operation => operation.getContext().clientName === 'cms',
    retryLink.concat(cmsLink),
    retryLink.concat(httpLink),
  )

  // error link sits outside the retries so a request only logs once it has finally failed
  return errorLink.concat(directionalLink)
}

export const apolloClient = new ApolloClient({
  link: getLink(),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          allPosts: concatPagination(),
        },
      },
    },
  }),
})

